import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { billService } from '../../services/billService';
import { Bill, BillFilters } from '../../types/bill';
import { formatCurrency, formatDate } from '../../utils/formatters';

interface BillListProps {
  filters: BillFilters;
  onView: (bill: Bill) => void;
  onEdit: (bill: Bill) => void;
  onPay: (bill: Bill) => void;
  expandedBillId: string | null;
}

const statusStyle: Record<string, React.CSSProperties> = {
  DRAFT:          { background: 'rgba(120,120,140,0.15)', color: '#9a9ab0', border: '1px solid rgba(120,120,140,0.25)' },
  PENDING:        { background: 'rgba(92,78,51,0.22)', color: '#d97706', border: '1px solid rgba(92,78,51,0.35)' },
  APPROVED:       { background: 'rgba(90,140,201,0.12)', color: '#5a8cc9', border: '1px solid rgba(90,140,201,0.25)' },
  PAID:           { background: 'rgba(90,201,140,0.12)', color: '#5ac98c', border: '1px solid rgba(90,201,140,0.25)' },
  PARTIALLY_PAID: { background: 'rgba(92,78,51,0.22)', color: '#d97706', border: '1px solid rgba(92,78,51,0.35)' },
  OVERDUE:        { background: 'rgba(201,90,90,0.12)', color: '#c95a5a', border: '1px solid rgba(201,90,90,0.25)' },
  CANCELLED:      { background: 'rgba(80,80,80,0.15)', color: '#555570', border: '1px solid rgba(80,80,80,0.2)' },
};

const thStyle: React.CSSProperties = {
  padding: '10px 16px',
  fontFamily: "'Inter', sans-serif",
  fontSize: 9,
  fontWeight: 500,
  letterSpacing: '0.22em',
  textTransform: 'uppercase',
  color: '#6b6a5c',
  borderBottom: '1px solid rgba(92,78,51,0.15)',
  whiteSpace: 'nowrap',
  textAlign: 'left',
};

const tdStyle: React.CSSProperties = {
  padding: '12px 16px',
  fontFamily: "'Inter', sans-serif",
  fontSize: 12,
  color: '#5b5a52',
  borderBottom: '1px solid rgba(241,245,249,1)',
  verticalAlign: 'middle',
};

export const BillList: React.FC<BillListProps> = ({ filters, onView, onEdit, onPay, expandedBillId }) => {
  const { data, isLoading, error } = useQuery({
    queryKey: ['bills', filters],
    queryFn: () => billService.getBills(filters)
  });

  const handleDownloadAttachment = async (billId: string, attachmentId: string, originalName: string) => {
    try {
      const blob = await billService.downloadAttachment(billId, attachmentId);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = originalName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading attachment:', error);
      alert('Failed to download attachment');
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  if (isLoading) {
    return (
      <div style={{ background: '#ffffff', border: '1px solid rgba(92,78,51,0.15)', borderRadius: 6 }}>
        <div style={{ padding: '16px 20px', borderBottom: '1px solid rgba(92,78,51,0.15)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 20, fontWeight: 300, color: '#1c2033' }}>Bills</div>
        </div>
        <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 10 }}>
          {[...Array(5)].map((_, i) => (
            <div key={i} style={{ height: 36, background: 'rgba(241,245,249,1)', borderRadius: 3 }} />
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    console.error('Bills loading error:', error);
    return (
      <div style={{ background: '#ffffff', border: '1px solid rgba(201,90,90,0.2)', borderRadius: 6, padding: 24, textAlign: 'center' }}>
        <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 13, color: '#c95a5a' }}>Error loading bills. Please try again.</p>
        <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, color: '#6b6a5c', marginTop: 4 }}>{(error as Error).message || 'Unknown error'}</p>
      </div>
    );
  }

  if (!data?.data.length) {
    return (
      <div style={{ background: '#ffffff', border: '1px solid rgba(92,78,51,0.15)', borderRadius: 6, padding: 48, textAlign: 'center' }}>
        <svg width="40" height="40" fill="none" stroke="rgba(217,119,6,0.25)" viewBox="0 0 24 24" style={{ margin: '0 auto 12px' }}>
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
        </svg>
        <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, letterSpacing: '0.2em', color: '#6b6a5c' }}>NO BILLS FOUND</p>
        <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 12, color: '#6b6a5c', marginTop: 6 }}>Get started by creating your first bill.</p>
      </div>
    );
  }

  return (
    <div style={{ background: '#ffffff', border: '1px solid rgba(92,78,51,0.15)', borderRadius: 6 }}>
      {/* Header */}
      <div style={{ padding: '16px 20px', borderBottom: '1px solid rgba(92,78,51,0.15)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 20, fontWeight: 300, color: '#1c2033' }}>Bills</div>
          <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, color: '#6b6a5c', marginTop: 2 }}>
            {data.pagination.totalItems ?? 0} total bills
          </div>
        </div>
      </div>

      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: 'rgba(201,165,90,0.02)' }}>
              <th style={thStyle}>Bill Details</th>
              <th style={thStyle}>Vendor</th>
              <th style={thStyle}>Job</th>
              <th style={thStyle}>Status</th>
              <th style={thStyle}>Amount</th>
              <th style={thStyle}>Dates</th>
              <th style={{ ...thStyle, textAlign: 'right' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {data.data.map((bill) => {
              const isOverdue = billService.isOverdue(bill.dueDate, bill.status);
              const isExpanded = expandedBillId === bill.id;
              const effectiveStatusStyle = isOverdue
                ? { background: 'rgba(201,90,90,0.12)', color: '#c95a5a', border: '1px solid rgba(201,90,90,0.25)' }
                : (statusStyle[bill.status] || statusStyle.DRAFT);

              return (
                <React.Fragment key={bill.id}>
                  <tr
                    style={{ transition: 'background 0.15s' }}
                    onMouseEnter={e => (e.currentTarget.style.background = 'rgba(248,250,252,1)')}
                    onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                  >
                    {/* Bill Details */}
                    <td style={tdStyle}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span style={{ color: '#1c2033', fontWeight: 500 }}>{bill.billNumber}</span>
                        {bill.attachments && bill.attachments.length > 0 && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                            <svg width="14" height="14" fill="none" stroke="rgba(217,119,6,0.45)" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                            </svg>
                            <span style={{ fontSize: 10, color: '#6b6a5c' }}>{bill.attachments.length}</span>
                          </div>
                        )}
                      </div>
                      {bill.notes && (
                        <div style={{ fontSize: 11, color: '#6b6a5c', marginTop: 3, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {bill.notes}
                        </div>
                      )}
                      {bill.isRecurring && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 4 }}>
                          <svg width="11" height="11" fill="none" stroke="#d97706" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                          </svg>
                          <span style={{ fontSize: 10, color: '#d97706' }}>Recurring</span>
                        </div>
                      )}
                    </td>

                    {/* Vendor */}
                    <td style={tdStyle}>
                      <div style={{ color: '#1c2033', fontWeight: 500 }}>{bill.vendor?.name}</div>
                      {bill.vendor?.email && (
                        <div style={{ fontSize: 11, color: '#6b6a5c', marginTop: 2 }}>{bill.vendor.email}</div>
                      )}
                    </td>

                    {/* Job */}
                    <td style={tdStyle}>
                      {bill.job ? (
                        <div>
                          <div style={{ color: '#1c2033', fontWeight: 500 }}>{bill.job.jobNumber}</div>
                          <div style={{ fontSize: 11, color: '#6b6a5c', marginTop: 2 }}>{bill.job.jobName}</div>
                          <div style={{ fontSize: 11, color: '#5a8cc9', marginTop: 1 }}>{bill.job.customer.name}</div>
                        </div>
                      ) : (
                        <span style={{ color: '#8a8778', fontSize: 11 }}>No Job</span>
                      )}
                      {bill.purchaseOrder && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 4 }}>
                          <svg width="11" height="11" fill="none" stroke="#5ac98c" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                          <span style={{ fontSize: 10, color: '#5ac98c' }}>PO: {bill.purchaseOrder.poNumber}</span>
                        </div>
                      )}
                    </td>

                    {/* Status */}
                    <td style={tdStyle}>
                      <span style={{
                        ...effectiveStatusStyle,
                        display: 'inline-block',
                        padding: '3px 10px',
                        borderRadius: 3,
                        fontFamily: "'Inter', sans-serif",
                        fontSize: 8,
                        letterSpacing: '0.15em',
                        textTransform: 'uppercase',
                      }}>
                        {isOverdue ? 'Overdue' : billService.formatStatus(bill.status)}
                      </span>
                    </td>

                    {/* Amount */}
                    <td style={tdStyle}>
                      <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 16, color: '#fbbf24' }}>
                        {formatCurrency(bill.total)}
                      </div>
                      {bill.paidAmount > 0 && (
                        <div style={{ fontSize: 11, color: '#5ac98c', marginTop: 2 }}>
                          Paid: {formatCurrency(bill.paidAmount)}
                        </div>
                      )}
                      {bill.remainingAmount > 0 && (
                        <div style={{ fontSize: 11, color: '#c95a5a', marginTop: 2 }}>
                          Due: {formatCurrency(bill.remainingAmount)}
                        </div>
                      )}
                    </td>

                    {/* Dates */}
                    <td style={tdStyle}>
                      <div style={{ fontSize: 11, color: '#5b5a52' }}>Bill: {formatDate(bill.billDate)}</div>
                      <div style={{ fontSize: 11, color: isOverdue ? '#c95a5a' : '#8a8778', fontWeight: isOverdue ? 500 : 400, marginTop: 2 }}>
                        Due: {formatDate(bill.dueDate)}
                      </div>
                    </td>

                    {/* Actions */}
                    <td style={{ ...tdStyle, textAlign: 'right' }}>
                      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
                        <button
                          onClick={() => onView(bill)}
                          style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, color: isExpanded ? '#c95a5a' : '#d97706', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                        >
                          {isExpanded ? 'Hide' : 'View'}
                        </button>
                        <button
                          onClick={() => onEdit(bill)}
                          style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, color: '#5b5a52', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                        >
                          Edit
                        </button>
                        {bill.remainingAmount > 0 && bill.status !== 'PAID' && bill.status !== 'CANCELLED' && (
                          <button
                            onClick={() => onPay(bill)}
                            style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, color: '#5ac98c', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                          >
                            Pay
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>

                  {/* Expanded View */}
                  {isExpanded && (
                    <tr>
                      <td colSpan={7} style={{ padding: '20px 24px', background: 'rgba(201,165,90,0.02)', borderTop: '1px solid rgba(92,78,51,0.15)', borderBottom: '1px solid rgba(92,78,51,0.15)' }}>
                        <div style={{ maxWidth: 860 }}>

                          {/* Bill Details Summary */}
                          <div style={{ marginBottom: 24 }}>
                            <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 17, fontWeight: 300, color: '#1c2033', marginBottom: 14 }}>
                              Bill Details
                            </div>
                            <div style={{
                              display: 'grid',
                              gridTemplateColumns: 'repeat(3, 1fr)',
                              gap: 16,
                              background: '#faf7ee',
                              border: '1px solid rgba(92,78,51,0.15)',
                              borderRadius: 4,
                              padding: 16
                            }}>
                              <div>
                                <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 8, letterSpacing: '0.18em', color: '#6b6a5c', textTransform: 'uppercase', marginBottom: 4 }}>Bill Number</div>
                                <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 12, color: '#1c2033' }}>{bill.billNumber}</div>
                              </div>
                              <div>
                                <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 8, letterSpacing: '0.18em', color: '#6b6a5c', textTransform: 'uppercase', marginBottom: 4 }}>Vendor</div>
                                <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 12, color: '#1c2033' }}>{bill.vendor?.name}</div>
                              </div>
                              <div>
                                <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 8, letterSpacing: '0.18em', color: '#6b6a5c', textTransform: 'uppercase', marginBottom: 4 }}>Job</div>
                                <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 12, color: '#1c2033' }}>
                                  {bill.job ? `${bill.job.jobNumber} - ${bill.job.jobName}` : 'No Job'}
                                </div>
                              </div>
                              <div>
                                <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 8, letterSpacing: '0.18em', color: '#6b6a5c', textTransform: 'uppercase', marginBottom: 4 }}>Status</div>
                                <div>
                                  <span style={{
                                    ...effectiveStatusStyle,
                                    display: 'inline-block',
                                    padding: '3px 10px',
                                    borderRadius: 3,
                                    fontFamily: "'Inter', sans-serif",
                                    fontSize: 8,
                                    letterSpacing: '0.15em',
                                    textTransform: 'uppercase',
                                  }}>
                                    {isOverdue ? 'Overdue' : billService.formatStatus(bill.status)}
                                  </span>
                                </div>
                              </div>
                              <div>
                                <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 8, letterSpacing: '0.18em', color: '#6b6a5c', textTransform: 'uppercase', marginBottom: 4 }}>Total Amount</div>
                                <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 15, color: '#fbbf24' }}>{formatCurrency(bill.total)}</div>
                              </div>
                              <div>
                                <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 8, letterSpacing: '0.18em', color: '#6b6a5c', textTransform: 'uppercase', marginBottom: 4 }}>Due Date</div>
                                <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 12, color: isOverdue ? '#c95a5a' : '#1c2033', fontWeight: isOverdue ? 500 : 400 }}>
                                  {formatDate(bill.dueDate)}
                                </div>
                              </div>
                              {bill.notes && (
                                <div style={{ gridColumn: '1 / -1' }}>
                                  <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 8, letterSpacing: '0.18em', color: '#6b6a5c', textTransform: 'uppercase', marginBottom: 4 }}>Notes</div>
                                  <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 12, color: '#5b5a52' }}>{bill.notes}</div>
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Attachments Section */}
                          <div>
                            <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 17, fontWeight: 300, color: '#1c2033', marginBottom: 14 }}>
                              Attachments ({bill.attachments?.length || 0})
                            </div>
                            {bill.attachments && bill.attachments.length > 0 ? (
                              <div style={{ background: '#faf7ee', border: '1px solid rgba(92,78,51,0.15)', borderRadius: 4 }}>
                                {bill.attachments.map((attachment, idx) => (
                                  <div
                                    key={attachment.id}
                                    style={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'space-between',
                                      padding: '12px 16px',
                                      borderBottom: idx < (bill.attachments?.length || 0) - 1 ? '1px solid rgba(241,245,249,1)' : 'none',
                                    }}
                                  >
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                      <svg width="28" height="28" fill="none" stroke="rgba(217,119,6,0.35)" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                      </svg>
                                      <div>
                                        <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 12, color: '#1c2033', fontWeight: 500 }}>{attachment.originalName}</div>
                                        <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, color: '#6b6a5c', marginTop: 2 }}>
                                          {formatFileSize(attachment.fileSize)} · Uploaded {formatDate(attachment.uploadedAt)}
                                        </div>
                                      </div>
                                    </div>
                                    <button
                                      onClick={() => handleDownloadAttachment(bill.id, attachment.id, attachment.originalName)}
                                      style={{ display: 'flex', alignItems: 'center', gap: 6, fontFamily: "'Inter', sans-serif", fontSize: 11, color: '#d97706', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                                    >
                                      <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                      </svg>
                                      <span>Download</span>
                                    </button>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <div style={{ background: '#faf7ee', border: '1px dashed rgba(92,78,51,0.25)', borderRadius: 4, padding: 28, textAlign: 'center' }}>
                                <svg width="36" height="36" fill="none" stroke="rgba(92,78,51,0.35)" viewBox="0 0 24 24" style={{ margin: '0 auto 10px' }}>
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                                <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 12, color: '#6b6a5c' }}>No attachments found for this bill</p>
                              </div>
                            )}
                          </div>

                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {data.pagination.totalPages > 1 && (
        <div style={{ padding: '12px 20px', borderTop: '1px solid rgba(92,78,51,0.15)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, color: '#6b6a5c' }}>
            Showing {((data.pagination.currentPage - 1) * data.pagination.itemsPerPage) + 1}–{Math.min(data.pagination.currentPage * data.pagination.itemsPerPage, data.pagination.totalItems)} of {data.pagination.totalItems}
          </span>
          <div style={{ display: 'flex', gap: 4 }}>
            {[...Array(data.pagination.totalPages)].map((_, i) => {
              const pageNum = i + 1;
              const isCurrentPage = pageNum === data.pagination.currentPage;
              return (
                <button
                  key={i}
                  style={{
                    padding: '4px 10px',
                    fontFamily: "'Inter', sans-serif",
                    fontSize: 9,
                    letterSpacing: '0.1em',
                    background: isCurrentPage ? 'linear-gradient(135deg, #d97706, #b45309)' : 'rgba(239,232,213,0.5)',
                    color: isCurrentPage ? '#1c2033' : '#8a8778',
                    border: isCurrentPage ? 'none' : '1px solid rgba(92,78,51,0.22)',
                    borderRadius: 3,
                    cursor: 'pointer',
                  }}
                >
                  {pageNum}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};
